import { Routes, Route, Navigate } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, RequireAuth, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import MobileWelcome from "@/pages/MobileWelcome";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import SignupFirm from "@/pages/SignupFirm";
import Privacy from "@/pages/Privacy";
import Support from "@/pages/Support";
import { isStandalone } from "@/lib/mobileApp";

import AppLayout from "@/layouts/AppLayout";
import PortalLayout from "@/layouts/PortalLayout";

import FirmDashboard from "@/pages/firm/Dashboard";
import FirmInbox from "@/pages/firm/Inbox";
import FirmClients from "@/pages/firm/Clients";import FirmClientDetail from "@/pages/firm/ClientDetail";
import FirmEnrolments from "@/pages/firm/Enrolments";
import FirmDocuments from "@/pages/firm/Documents";
import FirmRequests from "@/pages/firm/Requests";
import FirmTasks from "@/pages/firm/Tasks";
import FirmHelp from "@/pages/firm/Help";
import FirmSettings from "@/pages/firm/Settings";
import FirmAudit from "@/pages/firm/Audit";
import FirmIntegrations from "@/pages/firm/Integrations";
import SharePointSync from "@/pages/firm/SharePointSync";
import FirmInvites from "@/pages/firm/Invites";
import TaxLiability from "@/pages/firm/TaxLiability";
import TaxPosition from "@/pages/firm/TaxPosition";
import TaxPositionClient from "@/pages/firm/TaxPositionClient";
import TaxCheckQueue from "@/pages/firm/TaxCheckQueue";
import GstMonitor from "@/pages/firm/GstMonitor";
import RentalReminders from "@/pages/firm/RentalReminders";
import NavLayout from "@/pages/firm/NavLayout";
import WageCalculatorAdmin from "@/pages/firm/WageCalculatorAdmin";
import WageReports from "@/pages/firm/WageReports";
import WageReportBuilder from "@/pages/firm/WageReportBuilder";
import WageReportView from "@/pages/portal/WageReportView";
import SignInHelper from "@/pages/firm/SignInHelper";
import EmailCenter from "@/pages/firm/EmailCenter";
import JobFlow from "@/pages/firm/JobFlow";
import JobFlowSettings from "@/pages/firm/JobFlowSettings";
import JobDetail from "@/pages/firm/JobDetail";
import WageCalculator from "@/pages/public/WageCalculator";
import TaxProvisioning from "@/pages/firm/TaxProvisioning";
import TaxCalendar from "@/pages/firm/TaxCalendar";
import ProvisionalTax from "@/pages/firm/ProvisionalTax";
import ExceptionQueue from "@/pages/firm/ExceptionQueue";
import AuditReadiness from "@/pages/firm/AuditReadiness";
import FirmCashRegister from "@/pages/firm/CashRegister";
import Pipeline from "@/pages/firm/Pipeline";
import TaxReturns from "@/pages/firm/TaxReturns";
import Unmatched from "@/pages/firm/Unmatched";
import WorkQueue from "@/pages/firm/WorkQueue";
import ProductionLine from "@/pages/firm/ProductionLine";
import MorningBrief from "@/pages/firm/MorningBrief";
import Activate from "@/pages/Activate";

import PortalHome from "@/pages/portal/PortalDashboard";
import PortalUpload from "@/pages/portal/Upload";
import PortalRequests from "@/pages/portal/PortalRequests";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalHelp from "@/pages/portal/PortalHelp";
import PortalHistory from "@/pages/portal/PortalHistory";
import PortalCompliance from "@/pages/portal/PortalCompliance";
import PortalTaxReview from "@/pages/portal/PortalTaxReview";
import PortalTaxLiability, { PortalTaxLiabilityReport } from "@/pages/portal/PortalTaxLiability";
import PortalReturns from "@/pages/portal/PortalReturns";
import PortalCashRegister from "@/pages/portal/CashRegister";
import PortalCashOut from "@/pages/portal/PortalCashOut";
import { PortalClaim, PortalAfford, PortalAsk } from "@/pages/portal/PortalActions";
import { PortalPaySelf, PortalHire, PortalPriceChange, PortalGrowthPlan } from "@/pages/portal/PortalMoneyQuestions";
import PortalWealthPlan from "@/pages/portal/PortalWealthPlan";
import PortalBreakEven from "@/pages/portal/PortalBreakEven";
import ToolsWealthPlan from "@/pages/ToolsWealthPlan";
import EnrolSoleTrader from "@/pages/EnrolSoleTrader";
import EnrolCompany from "@/pages/EnrolCompany";
import EnrolSuccess from "@/pages/EnrolSuccess";
import FamilyCfoLanding from "@/pages/FamilyCfoLanding";
import FamilyCfoIntake from "@/pages/FamilyCfoIntake";
import FirmFamilyCfo from "@/pages/firm/FamilyCfo";
import FirmJobs from "@/pages/firm/Jobs";
import FirmEmailTemplates from "@/pages/firm/EmailTemplates";
import PortalDebtors from "@/pages/portal/PortalDebtors";
import PortalDebt from "@/pages/portal/PortalDebt";

import VersionCheckBanner from "@/components/VersionCheckBanner";

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) {
        // Inside the installed PWA / Emergent native mobile shell we swap the
        // desktop marketing Landing for a mobile-first welcome. The web
        // marketing surface stays untouched for browser visitors.
        return isStandalone() ? <MobileWelcome /> : <Landing />;
    }
    if (user.role === "client") return <Navigate to="/portal" replace />;
    return <Navigate to="/app" replace />;
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster position="top-right" richColors closeButton />
                    <VersionCheckBanner />
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/client/login" element={<Navigate to="/login" replace />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/signup/firm" element={<SignupFirm />} />
                        <Route path="/tools/wealth-plan" element={<ToolsWealthPlan />} />
                        <Route path="/wage-calculator" element={<WageCalculator />} />
                        <Route path="/portal/wage-report/:token" element={<WageReportView />} />
                        <Route path="/enrol/sole-trader" element={<EnrolSoleTrader />} />
                        <Route path="/enrol/company" element={<EnrolCompany />} />
                        <Route path="/enrol/success" element={<EnrolSuccess />} />
                        <Route path="/family-cfo" element={<FamilyCfoLanding />} />
                        <Route path="/family-cfo/intake" element={<FamilyCfoIntake />} />
                        <Route path="/activate/:token" element={<Activate />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/support" element={<Support />} />

                        {/* Firm staff/admin dashboard */}
                        <Route
                            path="/app"
                            element={
                                <RequireAuth roles={["firm_admin", "firm_staff", "super_admin"]}>
                                    <AppLayout />
                                </RequireAuth>
                            }
                        >
                            <Route index element={<FirmDashboard />} />
                            <Route path="inbox" element={<FirmInbox />} />
                            <Route path="clients" element={<FirmClients />} />
                            <Route path="enrolments" element={<FirmEnrolments />} />
                            <Route path="family-cfo" element={<FirmFamilyCfo />} />
                            <Route path="jobs" element={<FirmJobs />} />
                            <Route path="pipeline" element={<Pipeline />} />
                            <Route path="tax-returns" element={<TaxReturns />} />
                            <Route path="unmatched" element={<Unmatched />} />
                            <Route path="work-queue" element={<WorkQueue />} />
                            <Route path="production-line" element={<ProductionLine />} />
                            <Route path="morning-brief" element={<MorningBrief />} />
                            <Route path="clients/:clientId" element={<FirmClientDetail />} />
                            <Route path="documents" element={<FirmDocuments />} />
                            <Route path="requests" element={<FirmRequests />} />
                            <Route path="tasks" element={<FirmTasks />} />
                            <Route path="tax-liability" element={<TaxLiability />} />
                            <Route path="tax-position" element={<TaxPosition />} />
                            <Route path="tax-position/check-queue" element={<TaxCheckQueue />} />
                            <Route path="tax-position/clients/:clientId" element={<TaxPositionClient />} />
                            <Route path="gst-monitor" element={<GstMonitor />} />
                            <Route path="rental-reminders" element={<RentalReminders />} />
                            <Route path="tax-provisioning" element={<TaxProvisioning />} />
                            <Route path="tax-calendar" element={<TaxCalendar />} />
                            <Route path="provisional-tax" element={<ProvisionalTax />} />
                            <Route path="exception-queue" element={<ExceptionQueue />} />
                            <Route path="audit-readiness" element={<AuditReadiness />} />
                            <Route path="cash-register" element={<FirmCashRegister />} />
                            <Route path="help" element={<FirmHelp />} />
                            <Route path="audit" element={<FirmAudit />} />
                            <Route path="integrations" element={<FirmIntegrations />} />
                            <Route path="sharepoint" element={<SharePointSync />} />
                            <Route path="invites" element={<FirmInvites />} />
                            <Route path="settings" element={<FirmSettings />} />
                            <Route path="settings/navigation-layout" element={<NavLayout />} />
                            <Route path="settings/sign-in-helper" element={<SignInHelper />} />
                            <Route path="wage-calculator" element={<WageCalculatorAdmin />} />
                            <Route path="wage-reports" element={<WageReports />} />
                            <Route path="wage-reports/:id" element={<WageReportBuilder />} />
                            <Route path="email-center" element={<EmailCenter />} />
                            <Route path="job-flow" element={<JobFlow />} />
                            <Route path="job-flow/settings" element={<JobFlowSettings />} />
                            <Route path="job-flow/:id" element={<JobDetail />} />
                            <Route path="settings/email-templates" element={<FirmEmailTemplates />} />
                        </Route>

                        {/* Client portal — RequireAuth is intentionally NOT
                            role-gated here. The layout renders a friendly
                            "please use the desktop admin" screen for firm
                            users, which is important on the native mobile
                            build where /app/* routes don't exist. */}
                        <Route
                            path="/portal"
                            element={
                                <RequireAuth>
                                    <PortalLayout />
                                </RequireAuth>
                            }
                        >
                            <Route index element={<PortalHome />} />
                            <Route path="upload" element={<PortalUpload />} />
                            <Route path="requests" element={<PortalRequests />} />
                            <Route path="messages" element={<PortalMessages />} />
                            <Route path="help" element={<PortalHelp />} />
                            <Route path="history" element={<PortalHistory />} />
                            <Route path="compliance" element={<PortalCompliance />} />
                            <Route path="tax-position" element={<PortalTaxLiability />} />
                            <Route path="tax-liability" element={<PortalTaxLiability />} />
                            <Route path="tax-liability/reports/:reportId" element={<PortalTaxLiabilityReport />} />
                            <Route path="tax/review/:jobId" element={<PortalTaxReview />} />
                            <Route path="returns" element={<PortalReturns />} />
                            <Route path="cash-register" element={<PortalCashRegister />} />
                            <Route path="cash-out" element={<PortalCashOut />} />
                            <Route path="claim" element={<PortalClaim />} />
                            <Route path="afford" element={<PortalAfford />} />
                            <Route path="ask" element={<PortalAsk />} />
                            <Route path="debtors" element={<PortalDebtors />} />
                            <Route path="debt" element={<PortalDebt />} />
                            <Route path="pay-self" element={<PortalPaySelf />} />
                            <Route path="hire" element={<PortalHire />} />
                            <Route path="price-change" element={<PortalPriceChange />} />
                            <Route path="growth-plan" element={<PortalGrowthPlan />} />
                            <Route path="wealth-plan" element={<PortalWealthPlan />} />
                            <Route path="break-even" element={<PortalBreakEven />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
